import { useEffect, useState } from "react";
import { api, unwrapObject } from "@/lib/api";

/** One answer from Ask AI: the search it ran (`plan`), what it found, and how it read the question */
export interface Answer {
  suggestion_id: string | null;
  /** The same question was answered earlier today: its search was reused, no AI call */
  reused?: boolean;
  question: string;
  /** The search the AI chose; saved as a report, edited on the report screen and re-run without the AI */
  plan?: Plan;
  open_in?: { path: string; label: string; config: Record<string, unknown>; exact: boolean; missing: string[] } | null;
  answerable: boolean;
  reply?: string;
  title: string;
  entity?: string;
  measure?: Measure;
  answer?: string;
  count?: number;
  total?: number | null;
  value?: number | null;
  value_label?: string;
  value_money?: boolean;
  rate?: number | null;
  read?: string[];
  chips?: { key: string; label: string }[];
  unmatched?: string[];
  groups?: Group[];
  group_label?: string | null;
  group2_label?: string | null;
  group_kind?: "time" | "stage" | "category" | null;
  chart?: Chart;
  compare?: { label: string; from: string; to: string; count: number; value: number | null; change_pct: number | null } | null;
  target?: { label: string; period: string; amount: number; achieved: number; pct: number | null; money: boolean } | null;
  columns?: { key: string; label: string; type: FieldType }[];
  date_label?: string;
  rows?: Row[];
}
export interface Group {
  key: string;
  label: string;
  key2?: string;
  label2?: string;
  count: number;
  sum: number | null;
  avg: number | null;
  rate: number | null;
  value: number | null;
  qty?: number;
  prev?: number | null;
  target?: number | null;
}
export interface Row {
  id: string;
  title: string;
  subtitle: string;
  amount: number | null;
  status: string | null;
  date: string | null;
  owner: string | null;
  link: string;
  values?: Record<string, string | number | null>;
}
export type Measure = "LIST" | "COUNT" | "TOTAL" | "AVERAGE" | "RATE";
export type Chart = "AUTO" | "BAR" | "LINE" | "PIE" | "FUNNEL" | "TABLE";
export type FieldType = "text" | "number" | "money" | "date" | "choice" | "person" | "bool";
export type Op = "IS" | "IS_NOT" | "CONTAINS" | "NOT_CONTAINS" | "EMPTY" | "NOT_EMPTY" | "GTE" | "LTE" | "BETWEEN" | "BEFORE" | "AFTER" | "IN_LAST_DAYS" | "IN_NEXT_DAYS" | "OLDER_THAN_DAYS" | "YES" | "NO";
export interface Filter {
  field: string;
  op: Op;
  values: string[];
}
export interface LinkCheck {
  link: string;
  has: boolean;
  within_days: number | null;
}
/** The search behind an answer (AskCrm.service PlanSchema); only the parts the report screen changes are spelled out */
export interface Plan extends Record<string, unknown> {
  entity: string;
  measure: Measure;
  value_field: string | null;
  rate_statuses: string[];
  group_by: string;
  group_by2: string;
  filters: Filter[];
  links: LinkCheck[];
  columns: string[];
  date_field: string;
  date_preset: string;
  date_days: number | null;
  date_from: string | null;
  date_to: string | null;
  compare: "NONE" | "PREVIOUS_PERIOD" | "LAST_YEAR";
  with_target: boolean;
  sort: string;
  sort_field: string | null;
  chart: Chart;
  limit: number;
  title: string;
}

export interface Option {
  value: string;
  label: string;
}
export interface FieldDef {
  key: string;
  label: string;
  type: FieldType;
  group: boolean;
  ops: Op[];
  options?: Option[];
}
export interface EntityDef {
  key: string;
  label: string;
  noun: [string, string];
  statuses: Option[];
  amount: boolean;
  target: boolean;
  dates: Option[];
  fields: FieldDef[];
  links: { key: string; label: string; dated: boolean }[];
  groups: { key: string; label: string }[];
  values: { key: string; label: string; money: boolean }[];
}
export interface Catalog {
  entities: EntityDef[];
  people: { id: string; name: string }[];
  me: string;
}

/** A plan from an older answer, with every part the report screen edits filled in */
export function fullPlan(p: Record<string, unknown>): Plan {
  return {
    value_field: null,
    rate_statuses: [],
    group_by: "NONE",
    group_by2: "NONE",
    filters: [],
    links: [],
    columns: [],
    date_field: "NONE",
    date_preset: "NONE",
    date_days: null,
    date_from: null,
    date_to: null,
    compare: "NONE",
    with_target: false,
    sort: "NEWEST",
    sort_field: null,
    chart: "AUTO",
    limit: 10,
    title: "",
    ...p,
  } as unknown as Plan;
}

/** Takes one chip's condition off the search ("filter:2" is the third extra filter) */
export function withoutChip(p: Plan, key: string): Plan {
  const next: Plan = { ...p };
  const [kind, i] = key.split(":");
  switch (kind) {
    case "statuses":
    case "stages":
    case "owners":
    case "sources":
    case "priorities":
      next[kind] = [];
      break;
    case "city":
    case "search":
      next[kind] = null;
      break;
    case "amount":
      next.amount_min = null;
      next.amount_max = null;
      break;
    case "date":
      next.date_field = "NONE";
      next.date_preset = "NONE";
      next.date_from = null;
      next.date_to = null;
      next.compare = "NONE";
      break;
    case "idle":
      next.idle_days_min = null;
      break;
    case "overdue":
      next.overdue = false;
      break;
    case "filter":
      next.filters = p.filters.filter((_, n) => n !== Number(i));
      break;
    case "link":
      next.links = p.links.filter((_, n) => n !== Number(i));
      break;
    case "group":
      next.group_by = "NONE";
      next.group_by2 = "NONE";
      break;
    case "compare":
      next.compare = "NONE";
      break;
    case "target":
      next.with_target = false;
      break;
    case "measure":
      next.measure = "LIST";
      next.value_field = null;
      next.rate_statuses = [];
      break;
  }
  return next;
}

/** The words for each condition, as the filter builder offers them */
export const OP_LABEL: Record<Op, string> = {
  IS: "is",
  IS_NOT: "is not",
  CONTAINS: "contains",
  NOT_CONTAINS: "doesn't contain",
  EMPTY: "is empty",
  NOT_EMPTY: "is filled in",
  GTE: "at least",
  LTE: "at most",
  BETWEEN: "between",
  BEFORE: "before",
  AFTER: "after",
  IN_LAST_DAYS: "in the last … days",
  IN_NEXT_DAYS: "in the next … days",
  OLDER_THAN_DAYS: "more than … days ago",
  YES: "yes",
  NO: "no",
};
/** How many values a condition takes: none, one, two (a range) or several (a list to pick from) */
export function valueCount(op: Op, type: FieldType): 0 | 1 | 2 | "many" {
  if (op === "EMPTY" || op === "NOT_EMPTY" || op === "YES" || op === "NO") return 0;
  if (op === "BETWEEN") return 2;
  if ((op === "IS" || op === "IS_NOT") && (type === "choice" || type === "person" || type === "text")) return "many";
  return 1;
}

export const DATE_PRESETS: Option[] = [
  { value: "TODAY", label: "Today" },
  { value: "YESTERDAY", label: "Yesterday" },
  { value: "THIS_WEEK", label: "This week" },
  { value: "LAST_WEEK", label: "Last week" },
  { value: "THIS_MONTH", label: "This month" },
  { value: "LAST_MONTH", label: "Last month" },
  { value: "THIS_QUARTER", label: "This quarter" },
  { value: "THIS_YEAR", label: "This year" },
  { value: "LAST_N_DAYS", label: "Last 30 days" },
];

let cached: Promise<Catalog> | null = null;
/** What a report can be built from, loaded once per page load (it only changes with permissions or settings) */
export function useAskCatalog(enabled = true): Catalog | null {
  const [cat, setCat] = useState<Catalog | null>(null);
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    cached ??= api
      .get("/api/Ai/Ask/Fields")
      .then((r) => unwrapObject<Catalog>(r))
      .catch((e) => {
        cached = null;
        throw e;
      });
    cached.then((c) => live && setCat(c)).catch(() => undefined);
    return () => {
      live = false;
    };
  }, [enabled]);
  return cat;
}

/** Runs an edited search again: no AI, free, and only over what this person may see */
export async function runPlan(plan: Plan): Promise<Answer> {
  return unwrapObject<Answer>(await api.post("/api/Ai/Ask/Run", { plan }));
}
